import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  validateEmail, 
  validatePassword, 
  validatePasswordConfirmation,
  getPasswordStrength 
} from '../utils/auth-validation';
import { AUTH_MESSAGES } from '../utils/auth-constants';
import { Eye, EyeOff } from 'lucide-react';

type AuthMode = 'login' | 'register' | 'reset';

interface EmailAuthFormProps {
  mode: AuthMode;
  onModeChange: (mode: AuthMode) => void;
  onSuccess?: () => void;
}

interface FormData {
  email: string;
  password: string;
  confirmPassword: string;
  name: string;
}

interface FormErrors {
  email?: string;
  password?: string[];
  confirmPassword?: string;
  name?: string;
  general?: string;
}

const EmailAuthForm: React.FC<EmailAuthFormProps> = ({ mode, onModeChange, onSuccess }) => {
  const { loginWithEmail, registerWithEmail, resetPassword, resendVerification } = useAuth();
  
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    // Email validation
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      newErrors.email = emailValidation.error;
    }
    
    // Password validation (for login and register)
    if (mode !== 'reset') {
      const passwordValidation = validatePassword(formData.password);
      if (!passwordValidation.isValid) {
        newErrors.password = passwordValidation.errors;
      }
      
      // Confirm password validation (for register only)
      if (mode === 'register') {
        const confirmPasswordValidation = validatePasswordConfirmation(
          formData.password, 
          formData.confirmPassword
        );
        if (!confirmPasswordValidation.isValid) {
          newErrors.confirmPassword = confirmPasswordValidation.error;
        }
        
        // Name validation
        if (!formData.name.trim()) {
          newErrors.name = 'Name is required';
        }
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    setErrors({});
    setSuccessMessage(null);
    
    try {
      switch (mode) {
        case 'login':
          await loginWithEmail(formData.email, formData.password);
          onSuccess?.();
          break;
          
        case 'register':
          await registerWithEmail(formData.email, formData.password, formData.name);
          setSuccessMessage(AUTH_MESSAGES.REGISTRATION_SUCCESS);
          // Reset form
          setFormData({ email: '', password: '', confirmPassword: '', name: '' });
          // Switch to login mode after successful registration
          setTimeout(() => {
            setSuccessMessage(null);
            onModeChange('login');
          }, 3000);
          break;
          
        case 'reset':
          await resetPassword(formData.email);
          setSuccessMessage(AUTH_MESSAGES.PASSWORD_RESET_SENT);
          // Reset form
          setFormData({ email: '', password: '', confirmPassword: '', name: '' });
          break;
      }
    } catch (error) {
      console.error('Auth form error:', error);
      setErrors({ 
        general: error instanceof Error ? error.message : AUTH_MESSAGES.LOGIN_FAILED 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear field-specific errors when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
    
    // Clear general error when user makes any change
    if (errors.general) {
      setErrors(prev => ({ ...prev, general: undefined }));
    }
  };

  const handleResendVerification = async () => {
    if (!formData.email) {
      setErrors({ email: 'Please enter your email address' });
      return;
    }
    
    try {
      setIsLoading(true);
      await resendVerification(formData.email);
      setSuccessMessage('Verification email sent! Please check your inbox.');
    } catch (error) {
      setErrors({ 
        general: error instanceof Error ? error.message : 'Failed to resend verification email' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getPasswordStrengthColor = (strength: string) => {
    switch (strength) {
      case 'weak': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'strong': return 'bg-green-500';
      default: return 'bg-gray-300';
    }
  };

  const getPasswordStrengthWidth = (strength: string) => {
    switch (strength) {
      case 'weak': return 'w-1/3';
      case 'medium': return 'w-2/3';
      case 'strong': return 'w-full';
      default: return 'w-0';
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Success Message */}
        {successMessage && (
          <div className="p-4 bg-green-50 border border-green-200 text-green-800 rounded-md">
            {successMessage}
          </div>
        )}
        
        {/* General Error */}
        {errors.general && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-800 rounded-md">
            {errors.general}
          </div>
        )}

        {/* Name field (register only) */}
        {mode === 'register' && (
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Full Name
            </label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                errors.name 
                  ? 'border-red-300 dark:border-red-600' 
                  : 'border-gray-300 dark:border-gray-600'
              } dark:bg-gray-700 dark:text-white`}
              placeholder="Enter your full name"
              disabled={isLoading}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.name}</p>
            )}
          </div>
        )}

        {/* Email field */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Email Address
          </label>
          <input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
              errors.email 
                ? 'border-red-300 dark:border-red-600' 
                : 'border-gray-300 dark:border-gray-600'
            } dark:bg-gray-700 dark:text-white`}
            placeholder="Enter your email"
            disabled={isLoading}
            autoComplete="email"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email}</p>
          )}
        </div>

        {/* Password field (not for reset mode) */}
        {mode !== 'reset' && (
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Password
            </label>
            <div className="mt-1 relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                className={`block w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  errors.password 
                    ? 'border-red-300 dark:border-red-600' 
                    : 'border-gray-300 dark:border-gray-600'
                } dark:bg-gray-700 dark:text-white`}
                placeholder="Enter your password"
                disabled={isLoading}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            
            {/* Password strength indicator (register only) */}
            {mode === 'register' && formData.password && (
              <div className="mt-2">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-gray-600 dark:text-gray-400">Password strength</span>
                  <span className={`text-xs capitalize ${
                    getPasswordStrength(formData.password) === 'strong' ? 'text-green-600' :
                    getPasswordStrength(formData.password) === 'medium' ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {getPasswordStrength(formData.password)}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      getPasswordStrengthColor(getPasswordStrength(formData.password))
                    } ${getPasswordStrengthWidth(getPasswordStrength(formData.password))}`}
                  ></div>
                </div>
              </div>
            )}
            
            {errors.password && (
              <div className="mt-1 space-y-1">
                {errors.password.map((error, index) => (
                  <p key={index} className="text-sm text-red-600 dark:text-red-400">{error}</p>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Confirm Password field (register only) */}
        {mode === 'register' && (
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Confirm Password
            </label>
            <div className="mt-1 relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                className={`block w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  errors.confirmPassword 
                    ? 'border-red-300 dark:border-red-600' 
                    : 'border-gray-300 dark:border-gray-600'
                } dark:bg-gray-700 dark:text-white`}
                placeholder="Confirm your password"
                disabled={isLoading}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                               {showConfirmPassword ? (
                 <EyeOff className="h-5 w-5 text-gray-400" />
               ) : (
                 <Eye className="h-5 w-5 text-gray-400" />
               )}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.confirmPassword}</p>
            )}
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {mode === 'login' ? 'Signing in...' : mode === 'register' ? 'Creating account...' : 'Sending email...'}
            </>
          ) : (
            <>
              {mode === 'login' ? 'Sign In' : mode === 'register' ? 'Create Account' : 'Reset Password'}
            </>
          )}
        </button>

        {/* Mode switching and additional actions */}
        <div className="text-center space-y-2">
          {mode === 'login' && (
            <>
              <button
                type="button"
                onClick={() => onModeChange('reset')}
                className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400"
                disabled={isLoading}
              >
                Forgot your password?
              </button>
              <div>
                <span className="text-sm text-gray-600 dark:text-gray-400">Don't have an account? </span>
                <button
                  type="button"
                  onClick={() => onModeChange('register')}
                  className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400"
                  disabled={isLoading}
                >
                  Sign up
                </button>
              </div>
              <div>
                <button
                  type="button"
                  onClick={handleResendVerification}
                  className="text-sm text-gray-600 hover:text-gray-500 dark:text-gray-400"
                  disabled={isLoading}
                >
                  Resend verification email
                </button>
              </div>
            </>
          )}
          
          {mode === 'register' && (
            <div>
              <span className="text-sm text-gray-600 dark:text-gray-400">Already have an account? </span>
              <button
                type="button"
                onClick={() => onModeChange('login')}
                className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400"
                disabled={isLoading}
              >
                Sign in
              </button>
            </div>
          )}
          
          {mode === 'reset' && (
            <div>
              <span className="text-sm text-gray-600 dark:text-gray-400">Remember your password? </span>
              <button
                type="button"
                onClick={() => onModeChange('login')}
                className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400"
                disabled={isLoading}
              >
                Sign in
              </button>
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default EmailAuthForm; 